import { gql } from '../__generated__';

gql(/* GraphQL */ `
  fragment RevisionUserItem on User {
    subjectId
    name
    email
  }
`);

gql(/* GraphQL */ `
  fragment ChecklistTaskItem on ChecklistTask {
    id
    name
    types
    completed
    completedAt
    completedBy {
      subjectId
      name
    }
    notRelevant
    comment
    allowNotRelevant
    requireCommentWhenNotRelevant
    state
    weight
    startedAt
    startedBy {
      subjectId
      name
    }
  }
`);

gql(/* GraphQL */ `
  fragment ChecklistCheckItemItem on ChecklistCheckItem {
    id
    name
    state
    checked
    checkedAt
    checkedBy {
      subjectId
      name
    }
    notRelevant
    comment
    allowNotRelevant
    requireCommentWhenNotRelevant
  }
`);

gql(/* GraphQL */ `
  fragment ChecklistModelItem on ChecklistModel {
    id
    version
    latestVersion
    numberOfTasks
    completedTasks
    milestones {
      name
      number
      numberOfTasks
      completedTasks
      tasks {
        ...ChecklistTaskItem
      }
    }
    checkItems {
      ...ChecklistCheckItemItem
    }
  }
`);

gql(/* GraphQL */ `
  fragment ProjectLeadAssessmentItem on RevisionProjectLeadAssessment {
    id
    text
    createdAt
    createdByUser {
      subjectId
      name
    }
    updatedAt
  }
`);

gql(/* GraphQL */ `
  fragment RevisionModelItem on RevisionModel {
    id
    guideId
    type
    progressResponsibility
    lastActivityAt
    projectLeadAssessments {
      ...ProjectLeadAssessmentItem
    }
    title
    workingTitle
    freeText
    checklistId
    scheduledEndDate
    createdAt
    createdById
    professionQualityAssurers {
      id
      name
    }
    authors {
      id
      name
    }
    projectLeads {
      id: subjectId
      name
    }
    internalQualityAssurers {
      id: subjectId
      name
    }
    peerReviewers {
      id: subjectId
      name
    }
    dacs {
      id: subjectId
      name
    }
    publishingEditors {
      id: subjectId
      name
    }
    maconomyProjectNumber
    progress
    currentPhase
    inProgressTasks
    guide {
      docName
      docNumber
      docTitle
      mainCategory {
        ownerId
        title
      }
      ownerId
    }
    createdByUser {
      subjectId
      name
    }
    canceledAt
    canceledByUser {
      subjectId
      name
    }
    completedAt
    publishedVersion
    publishedAt
    checklist {
      ...ChecklistModelItem
    }
    commentToOrganization
  }
`);

export const REVISION_BY_ID = gql(/* GraphQL */ `
  query GetRevision($revisionId: UUID!) {
    revision(id: $revisionId) {
      ...RevisionModelItem
    }
  }
`);

export const GET_PEOPLE_FOR_ROLES = gql(/* GraphQL */ `
  query GetPeopleForRoles {
    users(where: { canHaveARole: { eq: true } }, order: [{ name: ASC }]) {
      id: subjectId
      name
    }
    subjectResources(where: { isDeleted: { eq: false } }, order: [{ name: ASC }]) {
      id
      name
    }
  }
`);

export const SET_RESPONSIBLE = gql(/* GraphQL */ `
  mutation RevisionSetResponsible($input: RevisionSetResponsibleInput!) {
    revisionSetResponsible(input: $input) {
      revisionModel {
        ...RevisionModelItem
      }
    }
  }
`);

export const UPDATE_COMMENT_TO_ORGANIZATION = gql(/* GraphQL */ `
  mutation RevisionUpdateCommentToOrganization($input: RevisionUpdateCommentToOrganizationInput!) {
    revisionUpdateCommentToOrganization(input: $input) {
      revisionModel {
        ...RevisionModelItem
      }
    }
  }
`);

export const GET_CHANGES = gql(/* GraphQL */ `
  query GetRevisionChanges($id: UUID!, $first: Int) {
    changeModel: revisionChanges(id: $id, first: $first) {
      totalCount
      nodes {
        id
        description
        createdAt
        createdBy
      }
    }
  }
`);

export const SET_TASK_STATE = gql(/* GraphQL */ `
  mutation RevisionSetTaskState($input: RevisionSetTaskStateInput!) {
    revisionSetTaskState(input: $input) {
      revisionModel {
        ...RevisionModelItem
      }
      errors {
        code: __typename
        ... on NotRelevantNotAllowedError {
          message
        }
        ... on NotRelevantCommentRequiredError {
          message
        }
      }
    }
  }
`);

export const SET_CHECK_ITEM_STATE = gql(/* GraphQL */ `
  mutation RevisionSetCheckItemState($input: RevisionSetCheckItemStateInput!) {
    revisionSetCheckItemState(input: $input) {
      revisionModel {
        ...RevisionModelItem
      }
      errors {
        code: __typename
        ... on NotRelevantNotAllowedError {
          message
        }
        ... on NotRelevantCommentRequiredError {
          message
        }
      }
    }
  }
`);

export const UPDATE_TYPE = gql(/* GraphQL */ `
  mutation RevisionUpdateType($input: RevisionUpdateTypeInput!) {
    revisionUpdateType(input: $input) {
      revisionModel {
        ...RevisionModelItem
      }
    }
  }
`);

export const SET_PROGRESS_RESPONSIBILITY = gql(/* GraphQL */ `
  mutation RevisionSetProgressResponsibility($input: RevisionSetProgressResponsibilityInput!) {
    revisionSetProgressResponsibility(input: $input) {
      revisionModel {
        id
        progressResponsibility
      }
    }
  }
`);

export const ADD_PROJECT_LEAD_ASSESSMENT = gql(/* GraphQL */ `
  mutation RevisionAddProjectLeadAssessment($input: RevisionAddProjectLeadAssessmentInput!) {
    revisionAddProjectLeadAssessment(input: $input) {
      revisionModel {
        id
        projectLeadAssessments {
          ...ProjectLeadAssessmentItem
        }
      }
    }
  }
`);

export const UPDATE_PROJECT_LEAD_ASSESSMENT = gql(/* GraphQL */ `
  mutation RevisionUpdateProjectLeadAssessment($input: RevisionUpdateProjectLeadAssessmentInput!) {
    revisionUpdateProjectLeadAssessment(input: $input) {
      revisionModel {
        id
        projectLeadAssessments {
          ...ProjectLeadAssessmentItem
        }
      }
    }
  }
`);

export const REMOVE_PROJECT_LEAD_ASSESSMENT = gql(/* GraphQL */ `
  mutation RevisionRemoveProjectLeadAssessment($input: RevisionRemoveProjectLeadAssessmentInput!) {
    revisionRemoveProjectLeadAssessment(input: $input) {
      revisionModel {
        id
        projectLeadAssessments {
          ...ProjectLeadAssessmentItem
        }
      }
    }
  }
`);

export const GET_REVISIONS = gql(/* GraphQL */ `
  query GetAllRevisions {
    revisions(order: { scheduledEndDate: ASC }, where: { canceledAt: { eq: null } }) {
      ...RevisionModelItem
    }
  }
`);

export const UPDATE_WORKING_TITLE = gql(/* GraphQL */ `
  mutation RevisionUpdateWorkingTitle($input: RevisionUpdateWorkingTitleInput!) {
    revisionUpdateWorkingTitle(input: $input) {
      revisionModel {
        id
        workingTitle
      }
    }
  }
`);

export const UPDATE_FREE_TEXT = gql(/* GraphQL */ `
  mutation RevisionUpdateFreeText($input: RevisionUpdateFreeTextInput!) {
    revisionUpdateFreeText(input: $input) {
      revisionModel {
        id
        freeText
      }
    }
  }
`);

export const COMPLETE = gql(/* GraphQL */ `
  mutation RevisionComplete($input: RevisionCompleteInput!) {
    revisionComplete(input: $input) {
      revisionStatusChange {
        revision {
          id
          guideId
          completedAt
          completedById
        }
        guide {
          id
          ongoingRevision {
            id
          }
        }
      }
      errors {
        code: __typename
        ... on RevisionHasOpenTasksError {
          message
        }
      }
    }
  }
`);

export const CANCEL = gql(/* GraphQL */ `
  mutation RevisionCancel($input: RevisionCancelInput!) {
    revisionCancel(input: $input) {
      revisionStatusChange {
        revision {
          id
          canceledAt
          canceledByUser {
            subjectId
            name
          }
        }
        guide {
          id
          ongoingRevision {
            id
          }
        }
      }
    }
  }
`);

export const SET_SCHEDULED_END_DATE = gql(/* GraphQL */ `
  mutation RevisionSetScheduledEndDate($input: RevisionSetScheduledEndDateInput!) {
    revisionSetScheduledEndDate(input: $input) {
      revisionModel {
        id
        scheduledEndDate
      }
    }
  }
`);

export const SET_MACONOMY_PROJECT_NUMBER = gql(/* GraphQL */ `
  mutation RevisionSetMaconomyProjectNumber($input: RevisionSetMaconomyProjectNumberInput!) {
    revisionSetMaconomyProjectNumber(input: $input) {
      revisionModel {
        id
        maconomyProjectNumber
      }
    }
  }
`);

export const MIGRATE_CHECKLIST = gql(/* GraphQL */ `
  mutation RevisionMigrateChecklist($input: RevisionMigrateChecklistInput!) {
    revisionMigrateChecklist(input: $input) {
      revisionModel {
        ...RevisionModelItem
      }
      errors {
        code: __typename
        ... on ChecklistMappingMissingError {
          message
        }
        ... on ChecklistMigrationWouldLoseWorkError {
          message
        }
        ... on ChecklistAlreadyOnVersionError {
          message
        }
        ... on UnauthorizedAccessError {
          message
        }
      }
    }
  }
`);
